import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { Select } from "../../../../ui/components/Select";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherArrowLeft, FeatherUser, FeatherMail } from "@subframe/core";
import { PermissionCards } from "../components/PermissionCards";
import { useUserManagement } from "../hooks/useUserManagement";
import { PermissionRepositoryImpl } from "../../data/repositories/PermissionRepositoryImpl";
import { UserRepositoryImpl } from "../../data/repositories/UserRepositoryImpl";
import { useToast } from "../../../../shared/presentation/components";
import { useAuth } from "../../../../features/auth/presentation/hooks/useAuth";
import { UserEntity } from "../../domain/entities/UserEntity";
import { updateUser } from "../../data/api/usersApi";
import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

const permissionRepository = new PermissionRepositoryImpl();
const userRepository = new UserRepositoryImpl();

interface Role {
  id: number;
  role_name: string;
  is_manager_required: boolean | number | string; // Can be boolean, number (0/1), or string ("0"/"1")
  description?: string;
}

// Helper function to check if manager is required - SIMPLE AND DIRECT
const isManagerRequiredForRole = (role: Role | undefined): boolean => {
  if (!role) {
    return false;
  }

  const value = role.is_manager_required;

  // If null/undefined, return false
  if (value === null || value === undefined) {
    return false;
  }

  // Convert to string, trim, and check if it's "1" or "true"
  const strValue = String(value).trim().toLowerCase();
  if (strValue === "1" || strValue === "true") {
    return true;
  }

  // Check if it's the number 1
  if (typeof value === "number" && value === 1) {
    return true;
  }

  // Check if it's boolean true
  if (typeof value === "boolean" && value === true) {
    return true;
  }

  return false;
};

// Admin and CFO roles do not have a manager; disable Manager field and treat as null
const isAdminOrCFORole = (role: Role | undefined): boolean => {
  if (!role?.role_name) return false;
  const name = String(role.role_name).trim().toLowerCase();
  return name === "admin" || name === "cfo";
};

export const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { showSuccess, showError } = useToast();
  const { isAdmin } = useAuth();
  const { fetchAssignableManagers, assignableManagers } = useUserManagement();

  const [user, setUser] = useState<UserEntity | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(
    null,
  );
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Record<string, any>>(
    {},
  );
  const [userPermissions, setUserPermissions] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load user data, roles, and permissions
  const loadUserData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoadingData(true);
      setLoadingPermissions(true);

      const token = getToken();
      if (!token) {
        showError("Authentication required");
        return;
      }

      // Fetch roles if admin/CFO
      if (isAdmin()) {
        // Fetch roles from new endpoint
        const rolesResponse = await fetch(`${API_BASE_URL}/api/roles/list`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (rolesResponse.ok) {
          const rolesData = await rolesResponse.json();
          if (rolesData.success) {
            setRoles(rolesData.data);
          }
        }

        await fetchAssignableManagers();
      }

      // Fetch user by ID directly
      const foundUser = await userRepository.getUserById(parseInt(userId, 10));

      setUser(foundUser);
      // Set role_id if available
      if (foundUser.role_id) {
        setSelectedRoleId(foundUser.role_id);
      }
      // Note: If role_id is not available, role selection by name is handled
      // in the useEffect hook below after roles are loaded
      setSelectedManagerId(foundUser.manager_id || null);
    } catch (error: any) {
      console.error("Error loading user:", error);
      showError(error.message || "Failed to load user data");
      navigate("/admin/dashboard");
    } finally {
      setLoadingData(false);
      setLoadingPermissions(false);
    }
  }, [userId, navigate, showError, isAdmin, fetchAssignableManagers]);

  // Load user permissions for UI display only (read-only)
  useEffect(() => {
    const loadPermissions = async () => {
      if (!userId || !user || !selectedRoleId || roles.length === 0) return;

      try {
        setLoadingPermissions(true);

        // Get role name from roles list - use type-safe comparison
        const selectedRole = roles.find((r) => {
          if (selectedRoleId === null || selectedRoleId === undefined)
            return false;
          return (
            r.id === selectedRoleId ||
            Number(r.id) === Number(selectedRoleId) ||
            String(r.id) === String(selectedRoleId)
          );
        });
        if (!selectedRole) return;

        // Fetch role permissions
        const rolePerms = await permissionRepository.getRolePermissions(
          selectedRole.id,
        );

        // Convert to the format PermissionCards expects
        const rolePermsFormatted: Record<string, any> = {};
        const userPermsFormatted: Record<string, Record<string, boolean>> = {};

        Object.entries(rolePerms).forEach(
          ([moduleCode, moduleData]: [string, any]) => {
            rolePermsFormatted[moduleCode] = {
              module_name: moduleData.module_name,
              actions: {},
            };

            if (!userPermsFormatted[moduleCode]) {
              userPermsFormatted[moduleCode] = {};
            }

            Object.entries(moduleData.actions).forEach(
              ([actionCode, actionData]: [string, any]) => {
                rolePermsFormatted[moduleCode].actions[actionCode] = {
                  action_name: actionData.action_name,
                  is_granted: actionData.is_granted,
                  requires_review: actionData.requires_review,
                };

                // Set effective permissions (from role)
                userPermsFormatted[moduleCode][actionCode] =
                  actionData.is_granted;
              },
            );
          },
        );

        setRolePermissions(rolePermsFormatted);
        setUserPermissions(userPermsFormatted);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        showError("Failed to load user permissions");
      } finally {
        setLoadingPermissions(false);
      }
    };

    if (roles.length > 0 && selectedRoleId) {
      loadPermissions();
    }
  }, [userId, selectedRoleId, user, roles, showError]);

  // Initial load
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  // Update role_id when roles are loaded and user has role name
  useEffect(() => {
    if (user && roles.length > 0 && !selectedRoleId && user.role) {
      const roleByName = roles.find((r) => r.role_name === user.role);
      if (roleByName) {
        setSelectedRoleId(roleByName.id);
      }
    }
  }, [user, roles, selectedRoleId]);

  // Update role_id when roles are loaded and user has role_id
  useEffect(() => {
    if (user && roles.length > 0 && user.role_id && !selectedRoleId) {
      // Use type-safe comparison to find role
      const roleById = roles.find((r) => {
        return (
          r.id === user.role_id ||
          Number(r.id) === Number(user.role_id) ||
          String(r.id) === String(user.role_id)
        );
      });
      if (roleById) {
        setSelectedRoleId(roleById.id);
      }
    }
  }, [user, roles, selectedRoleId]);

  const handleRoleChange = (value: string) => {
    const roleId = value ? parseInt(value, 10) : null;
    setSelectedRoleId(roleId);
    // Use type-safe comparison to find role
    const newRole = roles.find((r) => {
      if (roleId === null) return false;
      return (
        r.id === roleId ||
        Number(r.id) === Number(roleId) ||
        String(r.id) === String(roleId)
      );
    });
    // Reset manager when role changes if new role doesn't require manager or is Admin/CFO
    const roleRequiresManager = isManagerRequiredForRole(newRole);
    const roleIsAdminOrCFO = isAdminOrCFORole(newRole);
    if (!roleRequiresManager || roleIsAdminOrCFO) {
      setSelectedManagerId(null);
    }
    // Reset permissions when role changes (they'll be reloaded)
    setUserPermissions({});
    // Clear errors
    if (errors.role_id) {
      setErrors((prev) => ({ ...prev, role_id: "" }));
    }
    if (errors.manager_id) {
      setErrors((prev) => ({ ...prev, manager_id: "" }));
    }
  };

  const handleManagerChange = (value: string) => {
    const managerId = value === "none" ? null : parseInt(value, 10);
    setSelectedManagerId(managerId);
    if (errors.manager_id) {
      setErrors((prev) => ({ ...prev, manager_id: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check if selected role requires manager
    if (selectedRoleId) {
      const selectedRole = roles.find((r) => {
        return (
          r.id === selectedRoleId ||
          Number(r.id) === Number(selectedRoleId) ||
          String(r.id) === String(selectedRoleId)
        );
      });
      const roleRequiresManager = isManagerRequiredForRole(selectedRole);
      if (roleRequiresManager && !selectedManagerId) {
        newErrors.manager_id = "Manager is required for this role";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId) return;

    if (!validateForm()) {
      return;
    }

    // Double-check manager requirement before submitting
    if (selectedRoleId) {
      const selectedRole = roles.find((r) => {
        return (
          r.id === selectedRoleId ||
          Number(r.id) === Number(selectedRoleId) ||
          String(r.id) === String(selectedRoleId)
        );
      });
      const roleRequiresManager = isManagerRequiredForRole(selectedRole);
      if (roleRequiresManager && !selectedManagerId) {
        setErrors((prev) => ({
          ...prev,
          manager_id: "Manager is required for this role",
        }));
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);

    try {
      // Admin/CFO: always send role_id and manager_id (backend expects both)
      const updateData: {
        role_id?: number;
        manager_id?: number | null;
      } = {};

      if (isAdmin()) {
        updateData.role_id = selectedRoleId ?? undefined;
        // Admin/CFO role: always send manager_id null; otherwise use selected value
        updateData.manager_id = isAdminOrCFORole(selectedRole)
          ? null
          : (selectedManagerId ?? null);
      }
      await updateUser(parseInt(userId, 10), updateData);
      showSuccess("User updated successfully");
      navigate("/admin/dashboard");
    } catch (error: any) {
      showError(error.message || "Failed to update user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/dashboard");
  };

  // Calculate if manager is required - use type-safe comparison
  const selectedRole = roles.find((r) => {
    if (selectedRoleId === null || selectedRoleId === undefined) return false;
    return (
      r.id === selectedRoleId ||
      Number(r.id) === Number(selectedRoleId) ||
      String(r.id) === String(selectedRoleId)
    );
  });
  const isManagerRequired = isManagerRequiredForRole(selectedRole);
  const isManagerDisabled = isAdminOrCFORole(selectedRole); // Admin/CFO: no manager, field disabled
  // Exclude the user being edited from the Manager dropdown (user cannot be their own manager)
  const managersForDropdown = assignableManagers.filter(
    (manager) => manager.id !== user?.id,
  );

  if (!user) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center gap-4 p-8">
          <span className="text-body font-body text-subtext-color">
            Loading user data...
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 p-8">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherArrowLeft />}
              onClick={handleCancel}
              disabled={isLoading}
            >
              Back
            </Button>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Edit User
              </span>
              <span className="text-body font-body text-subtext-color">
                Update user role and manager assignment. Permissions are managed
                through roles.
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col items-start gap-6"
        >
          {/* Basic Information Section */}
          <div className="flex w-full flex-col gap-6 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Basic Information
            </span>

            <TextField
              className="h-auto w-full flex-none"
              label="Username"
              helpText="Username cannot be changed"
              icon={<FeatherUser />}
            >
              <TextField.Input
                name="userName"
                type="text"
                value={user.userName || "N/A"}
                disabled={true}
                readOnly
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              label="Email Address"
              helpText="Email address cannot be changed"
              icon={<FeatherMail />}
            >
              <TextField.Input
                name="email"
                type="email"
                value={user.email}
                disabled={true}
                readOnly
              />
            </TextField>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body font-body text-default-font">
                Role *
              </span>
              {isAdmin() && !loadingData ? (
                <Select
                  label=""
                  placeholder="Select role"
                  className="h-auto w-full flex-none"
                  value={selectedRoleId?.toString() || ""}
                  onValueChange={handleRoleChange}
                  disabled={isLoading || loadingPermissions}
                >
                  {roles.map((role) => (
                    <Select.Item key={role.id} value={role.id.toString()}>
                      {role.role_name}
                    </Select.Item>
                  ))}
                </Select>
              ) : (
                <div className="text-body font-body text-default-font py-2">
                  {user.role}
                </div>
              )}
            </div>

            {isAdmin() && !loadingData && (
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-body font-body text-default-font">
                  {isManagerRequired ? "Manager *" : "Manager"}
                  {isManagerDisabled && (
                    <span className="text-caption font-caption text-subtext-color ml-1">
                      (not applicable for Admin/CFO)
                    </span>
                  )}
                </span>
                {isManagerRequired ? (
                  <Select
                    key={`manager-required-${selectedRoleId || "none"}`}
                    label=""
                    placeholder="Select manager *"
                    className="h-auto w-full flex-none"
                    value={selectedManagerId?.toString() || ""}
                    onValueChange={handleManagerChange}
                    disabled={
                      isLoading || loadingPermissions || isManagerDisabled
                    }
                  >
                    {managersForDropdown.length === 0 ? (
                      <Select.Item value="" disabled>
                        No managers available
                      </Select.Item>
                    ) : (
                      managersForDropdown.map((manager) => (
                        <Select.Item
                          key={manager.id}
                          value={manager.id.toString()}
                        >
                          {manager.userName || manager.email} ({manager.role})
                        </Select.Item>
                      ))
                    )}
                  </Select>
                ) : (
                  <Select
                    key={`manager-optional-${selectedRoleId || "none"}`}
                    label=""
                    placeholder="Select manager (optional)"
                    className="h-auto w-full flex-none"
                    value={selectedManagerId?.toString() || "none"}
                    onValueChange={handleManagerChange}
                    disabled={
                      isLoading || loadingPermissions || isManagerDisabled
                    }
                  >
                    <Select.Item value="none">No Manager</Select.Item>
                    {managersForDropdown.map((manager) => (
                      <Select.Item
                        key={manager.id}
                        value={manager.id.toString()}
                      >
                        {manager.userName || manager.email} ({manager.role})
                      </Select.Item>
                    ))}
                  </Select>
                )}
                {isManagerRequired &&
                  !selectedManagerId &&
                  !errors.manager_id && (
                    <span className="text-caption font-caption text-subtext-color">
                      Manager is required for this role
                    </span>
                  )}
                {errors.manager_id && (
                  <span className="text-caption font-caption text-error-700">
                    {errors.manager_id}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Permissions Section - Read Only */}
          {loadingPermissions ? (
            <div className="flex w-full items-center justify-center py-8">
              <span className="text-body font-body text-subtext-color">
                Loading permissions...
              </span>
            </div>
          ) : selectedRoleId && rolePermissions ? (
            <div className="flex w-full flex-col gap-6 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex flex-col gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Permissions
                </span>
                <p className="text-body font-body text-subtext-color">
                  Permissions are managed through role assignments and cannot be
                  overridden. To change permissions, update the user's role or
                  modify the role's permissions.
                </p>
              </div>
              <PermissionCards
                rolePermissions={rolePermissions}
                userPermissions={userPermissions}
                onChange={() => {}} // No-op since read-only
                readOnly={true}
              />
            </div>
          ) : null}

          {/* Actions */}
          <div className="flex w-full items-end justify-end gap-3 border-t border-solid border-neutral-border pt-6">
            <Button
              type="button"
              variant="neutral-secondary"
              onClick={handleCancel}
              disabled={isLoading || loadingPermissions || loadingData}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              disabled={isLoading || loadingPermissions || loadingData}
              loading={isLoading}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </DefaultPageLayout>
  );
};
